import { ConfigInterface } from '../_base/config.interface'

/**
 * Replaces ConfigService and its DI registration.
 *
 * Follows AuthorBase's configModule shape: a mutable module-level singleton plus an `init`,
 * rather than a class that gets registered as a container singleton and then imperatively
 * `configure()`d immediately afterwards.
 *
 * Also takes ownership of choosing *which* config file to load. That logic is currently
 * copy-pasted into all four apps' `src/constants.ts` as `requireConfigFile()`, and common-app
 * reaches up into app-reviewer's copy from two places — a layering violation this removes.
 */

const emptyConfig = (): ConfigInterface => ({
  axios: {
    url: ''
  },
  apolloClient: {
    url: ''
  },
  quadernoApiKey: '',
  stripeApiKey: ''
})

let GlobalConfig: ConfigInterface = emptyConfig()

/**
 * The variable middle of the config filename for the current build — `dev`, `testing`,
 * `build.master` and so on — matching the switch the apps have been duplicating.
 *
 * Returns only the middle, not the whole filename, and that matters: the `require` below has to
 * keep `booksprout-app/config.` and `.js` as *static* parts of the template literal. Webpack
 * infers the context it builds from the static prefix and suffix, so interpolating the whole
 * filename widens the context to every file under common-app — which drags the `.spec.ts` files
 * into the bundle, and with them vue-server-renderer and its Node builtins. `diContainer.ts`
 * carries a comment about hitting the same trap.
 */
export const resolveConfigSuffix = (
  environment = process.env.ENVIRONMENT,
  buildMode = process.env.BUILD_MODE
): string => {
  let configBuild = 'dev'
  let configMode: string | undefined

  switch (environment) {
    case 'build':
      configBuild = 'build'
      configMode = buildMode
      break
    case 'testing':
      configBuild = 'testing'
      break
  }

  return `${configBuild}${configMode ? '.' + configMode : ''}`
}

/**
 * The filename the current build resolves to. For diagnostics and tests — the `require` builds
 * its own literal, for the reason above.
 */
export const resolveConfigFileName = (
  environment = process.env.ENVIRONMENT,
  buildMode = process.env.BUILD_MODE
): string => {
  return `config.${resolveConfigSuffix(environment, buildMode)}.js`
}

const useConfig = () => {
  /**
   * Explicit config, for tests and for callers that already hold one.
   */
  const init = (config: ConfigInterface) => {
    GlobalConfig = config
  }

  /**
   * Loads and applies the config file for the current build.
   *
   * Keep `booksprout-app/config.` and `.js` static in the template: that is what confines
   * webpack's generated context to `config.*.js`. Interpolating the whole filename instead
   * builds a context over all of common-app and pulls the spec files into the bundle.
   */
  const initFromEnv = (): ConfigInterface => {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const config = require(`booksprout-app/config.${resolveConfigSuffix()}.js`).default as ConfigInterface
    init(config)

    return config
  }

  const reset = () => {
    GlobalConfig = emptyConfig()
  }

  /**
   * Accessor as well as the exported binding: the binding relies on ES module live bindings,
   * which hold under webpack but are easy to trip over in CommonJS interop, so anything inside
   * common-app should prefer this.
   */
  const config = (): ConfigInterface => GlobalConfig

  return {
    init,
    initFromEnv,
    reset,
    config
  }
}

export { useConfig, GlobalConfig }
